"use client";
import React from "react";
import {
  Grid,
  Box,
  Typography,
  FormControl,
  Radio,
  Card,
  CardContent,
  Checkbox,
  FormControlLabel,
  Chip,
  Button,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";

const innerCardContent = ({ plan, selectedPlan, handlePlanSelect  }) => {
  return (
    <CardContent sx={{ display: "flex", alignItems: "center" }}>
      <Radio
        checked={selectedPlan === plan.plan_name}
        onChange={() => handlePlanSelect(plan.plan_name)}
        value={plan.plan_name}
      />
      <Box>
        <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
          {plan.plan_name}
        </Typography>
        <Typography variant="body2" color="text.secondary">
          ${plan.plan_amount} / month
        </Typography>
      </Box>
    </CardContent>
  );
};

const SubscriptionType = ({
  numLocations = 1,
  selectedPlan,
  setSelectedPlan,
  enableFreeTrial,
  setEnableFreeTrial,
  plansData = [],
  isEdit = false,
  onEditLocations,
  currentPlanData,

  // Token mode props
  isTokenMode = false,
  selectedTokenPack = null,
}) => {
  // ------------------------------------------------------------
  // TOKEN MODE — ONLY SUMMARY BOX (EditStore handles layout)
  // ------------------------------------------------------------
  if (isTokenMode) {
    return (
      <Box sx={{ width: "100%" }}>
        <Card
          sx={{
            p: 3,
            border: "1px solid #e5e7eb",
            borderRadius: "7px",
            width: "100%",
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              textAlign: "center",
              fontWeight: 600,
              fontSize: "14px",
              mb: 2,
              color: "black",
            }}
          >
            Token Purchase Summary
          </Typography>

          {!selectedTokenPack ? (
            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ textAlign: "center" }}
            >
              Select a token pack to see summary →
            </Typography>
          ) : (
            <>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h5" fontWeight={700} sx={{ fontSize: "22px" }}>
                  ${selectedTokenPack.amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  One-Time Payment
                </Typography>
              </Box>

              <Box>
                <Box display="flex" justifyContent="space-between" mb={1.2}>
                  <Typography variant="body2" color="text.secondary">
                    Selected Pack
                  </Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedTokenPack.title}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1.2}>
                  <Typography variant="body2" color="text.secondary">Total Tokens</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {selectedTokenPack.tokens}
                  </Typography>
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <Typography variant="body1" fontWeight={700}>Total Payable</Typography>
                  <Typography variant="body1" fontWeight={700}>
                    ${selectedTokenPack.amount}
                  </Typography>
                </Box>
              </Box>
            </>
          )}
        </Card>
      </Box>
    );
  }

   // ------------------------------------------------------------
  // REGULAR SUBSCRIPTION
  // ------------------------------------------------------------
  const handlePlanSelect = (planName) => {
    setSelectedPlan(planName);
    // Reset free trial when switching away from Basic plan (only in create mode)
    if (!isEdit && planName !== "Basic") {
      setEnableFreeTrial(false);
    }
  };
  
  const handleFreeTrialChange = (event) => {
    setEnableFreeTrial(event.target.checked);
  };

  const currentPlanDetails = plansData.find((p) => p.plan_name === selectedPlan);
  const basePrice = currentPlanDetails ? parseFloat(currentPlanDetails.plan_amount) : 0;
  const totalAmount = basePrice * (numLocations || 1);
  const tax = 0;

  const totalPayable = totalAmount + tax;

  return (
    <Box sx={{ width: "100%" }}>
      <Typography variant="h6" sx={{ fontWeight: 700, mb: 2 }}>
        Choose Your Subscription
      </Typography>

      {/* Location panel in edit mode */}
      {isEdit && (
        <Card
          sx={{
            p: 2.5,
            mb: 3,
            backgroundColor: "#f9fafb",
            borderRadius: "8px",
            border: "1px solid #e5e7eb",
          }}
        >
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="subtitle1" fontWeight={600}>
                Locations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentPlanData?.plan_name} — ${currentPlanData?.plan_amount} per location
              </Typography>
            </Box>

            <Chip
              label="Current Plan"
              size="small"
              sx={{
                backgroundColor: "#e3f2fd",
                color: "#1976d2",
                fontWeight: 600,
              }}
            />
          </Box>

          <Box
            sx={{
              p: 2,
              backgroundColor: "#fff",
              borderRadius: "6px",
              border: "1px solid #e0e0e0",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Box>
              <Typography variant="body2" fontWeight={600}>
                Current Locations
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {numLocations} location{numLocations !== 1 ? "s" : ""}
              </Typography>
            </Box>
            <Button
              variant="outlined"
              startIcon={<EditIcon />}
              onClick={onEditLocations}
              sx={{
                textTransform: "none",
                borderColor: "#e0e0e0",
                color: "text.primary",
              }}
            >
              Edit Locations
            </Button>
          </Box>
        </Card>
      )}

      {/* MAIN LAYOUT — FIXED */}
      <div style={{ display: "flex", gap: "20px" }}>
        {/* LEFT column */}
        <Grid item xs={12} md={6} width={"50%"}>
          <FormControl fullWidth>
            {plansData?.length ? (
              plansData.map((plan) => (
                <Card
                  key={plan.plan_id}
                  onClick={() => handlePlanSelect(plan.plan_name)}
                  sx={{
                    mb: 2,
                    border:
                      selectedPlan === plan.plan_name
                        ? "2px solid var(--primaryColor)"
                        : "1px solid var(--borderColor)",
                    borderRadius: "7px",
                    cursor: "pointer",
                  }}
                >
                  {innerCardContent({ plan, selectedPlan, handlePlanSelect})}
                </Card>
              ))
            ) : (
              <Typography color="text.secondary">Loading plans...</Typography>
            )}
          </FormControl>
        </Grid>

        {/* RIGHT column */}
        <Grid item xs={12} md={6} width={"50%"}>
          {currentPlanDetails ? (
            <Card
              sx={{
                p: 3,
                backgroundColor: "#fff",
                border: "1px solid #e5e7eb",
                borderRadius: "7px",
              }}
            >
              <Typography
                variant="subtitle2"
                sx={{
                  textAlign: "center",
                  fontWeight: 600,
                  mb: 2,
                }}
              >
                Pricing Summary
              </Typography>
              <Box sx={{ textAlign: "center", mb: 3 }}>
                <Typography variant="h4" fontWeight={700}>
                  ${currentPlanDetails.plan_amount}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  / month
                </Typography>
              </Box>
              <Box>
                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Plan Selected</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {currentPlanDetails.plan_name}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Locations</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    {numLocations}
                  </Typography>
                </Box>

                <Box display="flex" justifyContent="space-between" mb={1}>
                  <Typography variant="body2">Amount</Typography>
                  <Typography variant="body2" fontWeight={600}>
                    ${totalAmount.toFixed(2)}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    mt: 2,
                    pt: 2,
                    borderTop: "1px solid #e5e7eb",
                  }}
                >
                  <Typography variant="body1" fontWeight={700}>Total Payable</Typography>
                  <Typography variant="body1" fontWeight={700}>
                    ${totalPayable.toFixed(2)}
                  </Typography>
                </Box>
                {/* Free Trial Checkbox - Only for Basic Plan in CREATE mode */}
                {!isEdit && currentPlanDetails.plan_name === "Basic" && (
                  <Box sx={{ mt: 2, mb: 2, p: 1.5, backgroundColor: "#f0f7ff", borderRadius: "5px" }}>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={enableFreeTrial}
                          onChange={handleFreeTrialChange}
                        />
                      }
                      label={
                        <Typography variant="body2">
                          Enable 14 days free trial. Start paying after the trial period ends.
                        </Typography>
                      }
                    />
                  </Box>
                )}
              </Box>
            </Card>
          ) : (
            <Typography color="text.secondary">
              Select a plan to view summary →
            </Typography>
          )}
        </Grid>
      </div>
    </Box>
  );
};

export default SubscriptionType;